import { LitElement, html, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { html as staticHtml, literal } from 'lit/static-html.js';
import type { StaticValue } from 'lit-html/static.js';
import { styles } from './sds-alert.styles';
import { SDS_VARIANTS, SDS_SIZES } from '../types';
import { SDSEventDispatcherMixin } from '../mixins/sds-event-dispatcher-mixin';

type ColorPalette = 'info' | 'success' | 'attention' | 'danger';
type Variants = Extract<SDS_VARIANTS, 'primary' | 'secondary'>;
type AlertSize = Extract<SDS_SIZES, 'lg' | 'md' | 'sm'>;

const alertIcons: Record<ColorPalette, string> = {
	info: 'InformationSquareFill-aksel-filled',
	success: 'check_circle-material-filled',
	attention: 'warning-material-filled',
	danger: 'dangerous-material-filled',
};

export interface SDSAlertProps {
	variant?: Variants;
	size?: AlertSize;
	'removable-label'?: string;
}

export class SDSAlertComponent extends SDSEventDispatcherMixin(LitElement) {
	static override styles = [styles];

	@property({ type: String, reflect: true }) variant: Variants = 'primary';
	@property({ type: String, reflect: true }) size: AlertSize = 'md';
	@property({ type: String, attribute: 'removable-label', reflect: true }) removableLabel?: string;
	@property({ type: String, attribute: 'data-palette', reflect: true }) palette: ColorPalette = 'info';

	protected iconTag: StaticValue = literal`sds-icon`;
	protected buttonTag: StaticValue = literal`sds-button`;

	private readonly _remove = () => {
		this.dispatchSheriffEvent('sds-remove');
	};

	private readonly _renderIcon = (iconName: string, size: string, cls?: string) => {
		return staticHtml`<${this.iconTag} ${cls ? `class=${cls}` : ''} name="${iconName}" size=${size}></${this.iconTag}>`;
	};

	private readonly _renderRemoveButton = (ariaLabel: string) => {
		return staticHtml`<${this.buttonTag}
		class="sds-alert__remove-btn"
		variant="tertiary"
		size="sm"
		?icon=${true}
		aria-label=${ariaLabel}
		@click=${this._remove}
		>
		${this._renderIcon('close-material-outlined', 'sm')}
		</${this.buttonTag}>`;
	};

	override render() {
		const iconSize = this.variant === 'primary' ? 'md' : this.size ?? 'md';
		const iconName = alertIcons[this.palette] ?? alertIcons['info'];

		return html`<div class="sds-alert">
			<div class="sds-alert__type">${this._renderIcon(iconName, iconSize, 'sds-alert__icon')}</div>
			<div class="sds-alert__content">
				<slot name="title"></slot>
				<slot name="message"></slot>
			</div>
			${this.removableLabel && this.variant === 'primary' ? html`<div class="sds-alert__actions">${this._renderRemoveButton(this.removableLabel)}</div>` : nothing}
		</div>`;
	}
}
